import { Bath, Trees, Armchair, Coffee, KeyRound, DoorOpen, Clock, Sparkles, ConciergeBell } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { useLang } from "../i18n";

const BLOCK_META = [
  {
    icon: Bath,
    image: "/images/bagno-camera-101-le-dolci-dimore.jpg",
    imageAltIt: "Il bagno privato con doccia della Camera 101",
    imageAltEn: "The private bathroom with shower of Room 101",
  },
  {
    icon: Coffee,
    imageKey: "breakfast",
    imageAltIt: "Il vassoio della colazione in camera de Le dolci Dimore: croissant, cereali, biscotti, miele e Nutella",
    imageAltEn: "The in-room breakfast tray at Le dolci Dimore: croissants, cereals, biscuits, honey and Nutella",
  },
  {
    icon: Trees,
    imageKey: "gardenSeating",
    imageAltIt: "Le sedute sotto l'ombrellone nel giardino de Le dolci Dimore",
    imageAltEn: "The seating under the parasol in the garden of Le dolci Dimore",
  },
  {
    icon: Armchair,
    imageKey: "reception",
    imageAltIt: "L'area comune de Le dolci Dimore con la porta a vetri sul giardino",
    imageAltEn: "The common area of Le dolci Dimore with the glass door onto the garden",
  },
  {
    icon: DoorOpen,
    image: "/images/camera-102-zona-giorno-le-dolci-dimore.jpg",
    imageAltIt: "La zona giorno della Camera 102 al primo piano della villa",
    imageAltEn: "The living corner of Room 102 on the first floor of the villa",
  },
  {
    icon: KeyRound,
    imageKey: "facadeDoor",
    imageAltIt: "La porta d'ingresso della villa de Le dolci Dimore",
    imageAltEn: "The entrance door of the Le dolci Dimore villa",
  },
];

const HOUR_ICONS = [Clock, Sparkles, ConciergeBell];

export default function Servizi() {
  const { lang, data, t } = useLang();
  const { IMAGES } = data;
  const p = t.servicesPage;

  const blockImage = (meta) => (meta.imageKey ? IMAGES[meta.imageKey] : meta.image);
  const blockAlt = (meta) => (lang === "en" ? meta.imageAltEn : meta.imageAltIt);

  return (
    <>
      <Seo title={p.seo.title} description={p.seo.description} path="/servizi-e-informazioni" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-14 md:pb-20">
        <Reveal>
          <p className="eyebrow mb-5">{p.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-ink max-w-3xl">
            {p.h1}
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-8 text-base md:text-lg text-body leading-relaxed max-w-2xl">
            {p.lead}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 space-y-16 md:space-y-24">
        {p.blocks.map((b, i) => {
          const meta = BLOCK_META[i];
          const Icon = meta.icon;
          return (
            <Reveal key={b.title}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2 md:col-start-7" : ""}`}>
                  <Icon className="w-6 h-6 text-terra" aria-hidden="true" />
                  <h2 className="font-serif text-2xl md:text-4xl text-ink mt-4">{b.title}</h2>
                  <p className="mt-5 text-sm md:text-base text-body leading-relaxed">{b.text}</p>
                </div>
                <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-8"}`}>
                  <div className="img-frame aspect-[4/3]">
                    <img src={blockImage(meta)} alt={blockAlt(meta)} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      <section className="bg-surface border-y border-sand">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-3 gap-10">
          {p.hours.map((hBlock, i) => {
            const Icon = HOUR_ICONS[i];
            return (
              <Reveal key={hBlock.title} delay={0.08 * i}>
                <Icon className="w-6 h-6 text-terra" aria-hidden="true" />
                <h2 className="font-serif text-2xl text-ink mt-4">{hBlock.title}</h2>
                <p className="mt-4 text-sm text-body leading-relaxed">
                  {hBlock.text ? (
                    hBlock.text
                  ) : (
                    <>
                      {hBlock.textPre}
                      <strong className="text-ink font-semibold">{hBlock.textStrong1}</strong>
                      {hBlock.textMid}
                      <strong className="text-ink font-semibold">{hBlock.textStrong2}</strong>
                      {hBlock.textPost}
                    </>
                  )}
                </p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTABand />
    </>
  );
}
